import type {
  BufferUpload,
  LocalFile,
  Upload as UploadEntity,
  Uploader,
} from '@buffer-mono/uploader'
import { Upload, UploadSource } from '@buffer-mono/uploader'

import type { GuestMedia } from '~publish/helpers/guestMedia'
import { isGuestMediaExpired } from '~publish/helpers/guestMedia'
import { getGuestMediaAccess } from '~publish/pages/Create/PublicComposerPage/guestMediaClient'

import type { PromotedGuestMedia } from './guestDraftHandoffJournal'

function createAbortError(): DOMException {
  return new DOMException('The draft import was cancelled', 'AbortError')
}

export class GuestMediaUnavailableError extends Error {}

export function isGuestMediaUnavailableError(
  error: unknown,
): error is GuestMediaUnavailableError {
  return error instanceof GuestMediaUnavailableError
}

function isInvalidAccessError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.message.startsWith('The provided URL') ||
      error.message.startsWith('The anonymous media upload'))
  )
}

function getImportFileName(media: GuestMedia): string {
  const extension = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
  }[media.mimeType]

  return `guest-${media.id}.${extension}`
}

function isMatchingFile(
  file: Pick<LocalFile, 'name' | 'size'> | Pick<UploadEntity, 'name' | 'size'>,
  expected: File,
): boolean {
  return file.name === expected.name && file.size === expected.size
}

function uploadGuestMediaFile(
  uploader: Pick<Uploader, 'addFiles' | 'off' | 'on' | 'removeUpload'>,
  file: File,
  signal: AbortSignal,
): Promise<BufferUpload> {
  if (signal.aborted) return Promise.reject(createAbortError())

  return new Promise<BufferUpload>((resolve, reject) => {
    let startedUpload: UploadEntity | undefined
    let settled = false

    const cleanup = (): void => {
      uploader.off('upload-started', handleUploadStarted)
      uploader.off('upload-finished', handleUploadFinished)
      uploader.off('upload-failed', handleUploadFailed)
      uploader.off('file-validation-failed', handleValidationFailed)
      uploader.off('upload-prevented', handleUploadPrevented)
      signal.removeEventListener('abort', handleAbort)
    }

    const fail = (error: Error): void => {
      if (settled) return
      settled = true
      cleanup()
      reject(error)
    }

    function handleUploadStarted(upload: UploadEntity): void {
      if (!isMatchingFile(upload, file)) return
      startedUpload = upload
      if (signal.aborted) uploader.removeUpload(upload)
    }

    function handleUploadFinished(upload: BufferUpload): void {
      if (!isMatchingFile(upload, file) || settled) return
      settled = true
      cleanup()
      resolve(upload)
    }

    function handleUploadFailed(upload: UploadEntity, error: Error): void {
      if (isMatchingFile(upload, file)) fail(error)
    }

    function handleValidationFailed(
      invalidFile: LocalFile,
      error: Error,
    ): void {
      if (isMatchingFile(invalidFile, file)) fail(error)
    }

    function handleUploadPrevented(
      error: Error,
      files: LocalFile[] = [],
    ): void {
      if (files.some((candidate) => isMatchingFile(candidate, file))) {
        fail(error)
      }
    }

    function handleAbort(): void {
      if (startedUpload) uploader.removeUpload(startedUpload)
      fail(createAbortError())
    }

    uploader.on('upload-started', handleUploadStarted)
    uploader.on('upload-finished', handleUploadFinished)
    uploader.on('upload-failed', handleUploadFailed)
    uploader.on('file-validation-failed', handleValidationFailed)
    uploader.on('upload-prevented', handleUploadPrevented)
    signal.addEventListener('abort', handleAbort, { once: true })

    void uploader
      .addFiles([file], {
        source: UploadSource.integration('guestDraftHandoff'),
      })
      .catch((error: unknown) => {
        fail(
          error instanceof Error
            ? error
            : new Error('The image upload could not start'),
        )
      })
  })
}

export async function promoteGuestDraftMedia(
  media: GuestMedia,
  uploader: Pick<Uploader, 'addFiles' | 'off' | 'on' | 'removeUpload'>,
  signal: AbortSignal,
): Promise<PromotedGuestMedia> {
  if (isGuestMediaExpired(media)) {
    throw new GuestMediaUnavailableError('The guest image has expired')
  }

  let accessUrl: string
  try {
    const access = await getGuestMediaAccess(media, { signal })
    accessUrl = access.accessUrl
  } catch (error) {
    if (isInvalidAccessError(error)) {
      throw new GuestMediaUnavailableError('The guest image is unavailable')
    }
    throw error
  }

  const response = await fetch(accessUrl, {
    credentials: 'omit',
    signal,
  })
  if ([403, 404, 410].includes(response.status)) {
    throw new GuestMediaUnavailableError('The guest image is unavailable')
  }
  if (!response.ok) throw new Error('The image could not be downloaded')

  const blob = await response.blob()
  if (blob.size === 0) throw new Error('The image is empty')

  const file = new File([blob], getImportFileName(media), {
    type: media.mimeType,
  })
  const upload = await uploadGuestMediaFile(uploader, file, signal)

  try {
    if (!Upload.isImage(upload) && !Upload.isGif(upload)) {
      throw new Error('The image upload returned an unsupported file')
    }

    const dimensions =
      upload.dimensions ??
      (media.width && media.height
        ? { width: media.width, height: media.height }
        : undefined)

    return {
      guestMediaId: media.id,
      sourceUploadedAt: media.uploadedAt,
      url: upload.url,
      thumbnailUrl: upload.url,
      mimeType: media.mimeType,
      ...(media.altText ? { altText: media.altText } : {}),
      ...(dimensions
        ? { width: dimensions.width, height: dimensions.height }
        : {}),
    }
  } finally {
    uploader.removeUpload(upload)
  }
}
